import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import { SelectFormInput, TextFormInput } from "src/components/form";
import { InstitutionFinanceBreadcrumbs } from "src/helpers/breadcrumbHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "src/helpers/lookupHelper";
import useNavigate from "src/hooks/useNavigate";
import { RegistrationFormComponent } from "src/interfaces/forms/IRegistrationComponent";
import { IInstitutionFinanceReduxFormState } from "src/reducers/reactReduxForms/institutionFinance";
import * as InstitutionFinanceActions from "../../../actions/institutionFinance";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { InstitutionSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import "./InstitutionFinancePage.scss";

interface InstitutionFinancePageProps {
    form: Dtos.InstitutionFinance & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: IInstitutionFinanceReduxFormState;
    institutionFinance: Dtos.InstitutionFinance;
    lookups: Dtos.Lookup[];
    loadingInstitutionFinance: boolean;
    loadInstitutionFinanceSuccess: boolean;
    loadInstitutionFinanceFailure: boolean;
    loadInstitutionFinanceErrorCode: string;
    savingInstitutionFinance: boolean;
    saveInstitutionFinanceSuccess: boolean;
    saveInstitutionFinanceFailure: boolean;
    validatingInstitutionFinance: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearInstitutionFinance: InstitutionFinanceActions.IInstitutionFinanceClearActionCreator;
    loadInstitutionFinance: InstitutionFinanceActions.IInstitutionFinanceLoadByInstitutionIdActionCreator;
    saveInstitutionFinance: InstitutionFinanceActions.IInstitutionFinanceSaveActionCreator;
    validateInstitutionFinance: InstitutionFinanceActions.IInstitutionFinanceValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.institutionFinance";

const InstitutionFinancePage: FC<InstitutionFinancePageProps> = ({
    form,
    formState,
    formProperties,
    institutionFinance,
    lookups,
    loadingInstitutionFinance,
    loadInstitutionFinanceFailure,
    loadInstitutionFinanceErrorCode,
    savingInstitutionFinance,
    saveInstitutionFinanceSuccess,
    saveInstitutionFinanceFailure,
    validatingInstitutionFinance,
    validationFailures,
    permissions,
    clearInstitutionFinance,
    loadInstitutionFinance,
    saveInstitutionFinance,
    validateInstitutionFinance,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { institutionId } = useParams<{ institutionId: string }>();
    const institutionIdNumber = parseInt(institutionId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load Institution Finance
    useEffect(() => {
        if (institutionIdNumber && !institutionFinance) {
            setBreadcrumbs(InstitutionFinanceBreadcrumbs(institutionIdNumber));
            setDrawerItems(
                InstitutionSubMenu(institutionIdNumber, permissions)
            );

            loadInstitutionFinance(institutionIdNumber);
        } else if (institutionFinance && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        institutionIdNumber,
        institutionFinance,
        permissions,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadInstitutionFinance,
        InstitutionFinanceBreadcrumbs,
        setFormInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionFinanceFailure) {
            navigate(`/error/${loadInstitutionFinanceErrorCode}`);
        }
    }, [
        loadInstitutionFinanceFailure,
        loadInstitutionFinanceErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (institutionIdNumber && institutionFinance?.institution) {
            setBreadcrumbs(
                InstitutionFinanceBreadcrumbs(
                    institutionIdNumber,
                    institutionFinance.institution?.name
                )
            );
        }
    }, [
        institutionIdNumber,
        institutionFinance,
        setBreadcrumbs,
        InstitutionFinanceBreadcrumbs,
    ]);

    // Reload institution finance after saving
    useEffect(() => {
        if (!savingInstitutionFinance && saveInstitutionFinanceSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Institution finance saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                const url = `/institutions`;
                navigate(url);
            } else if (!institutionIdNumber) {
                const url = `/institutions/${institutionIdNumber}/finance`;
                navigate(url);
                clearInstitutionFinance();
            }
        }
    }, [
        savingInstitutionFinance,
        saveInstitutionFinanceSuccess,
        institutionIdNumber,
        navigate,
        saveAndReturn,
        clearInstitutionFinance,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingInstitutionFinance && saveInstitutionFinanceFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingInstitutionFinance, saveInstitutionFinanceSuccess]);

    // Cleanup effect to clear the institution finance on component unmount
    useEffect(() => {
        return () => {
            clearInstitutionFinance();
            resetForm(reduxFormName);
        };
    }, [clearInstitutionFinance, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.InstitutionFinance =
                {} as Dtos.InstitutionFinance;

            if (!data.institutionId) {
                changes.institutionId = institutionIdNumber;
            }

            await saveInstitutionFinance(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.InstitutionFinance) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        const url = "/institutions";
        navigate(url);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.InstitutionFinance = {} as Dtos.InstitutionFinance;

        loadForm(
            reduxFormName,
            Object.assign({ ...institutionFinance }, extraData)
        );
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.InstitutionFinance =
                {} as Dtos.InstitutionFinance;

            if (form && hasBeenSubmitted && !validatingInstitutionFinance) {
                await validateInstitutionFinance(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                institutionIdNumber,
                null,
                permissions,
                Dtos.Permission.InstitutionFinanceUpdate,
                Dtos.Permission.InstitutionFinanceCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingInstitutionFinance ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "institutionFinance",
                                        {
                                            bctInvoicingPrefix: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "institutionFinance",
                                        {
                                            quickbooksTaxCodeId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "InstitutionFinance",
                                                                "quickbooksTaxCodeId",
                                                                lookups
                                                            ),
                                                            form.quickbooksTaxCodeId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "institutionFinance",
                                        {
                                            taxInvoiceName: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "institutionFinance",
                                        {
                                            fundingEmailAddress: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "institutionFinance",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        permissions,
        savingInstitutionFinance,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        institutionFinance,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupLabView}
            loading={loadingInstitutionFinance || savingInstitutionFinance}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<InstitutionFinancePageProps> => {
    let institutionFinance: Dtos.InstitutionFinance | undefined = !(
        state.institutionFinance.formData instanceof Array
    )
        ? state.institutionFinance.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        institutionFinance: institutionFinance!,
        loadingInstitutionFinance:
            state.institutionFinance.loadState &&
            state.institutionFinance.loadState.status == RequestState.Pending,
        loadInstitutionFinanceSuccess:
            state.institutionFinance.loadState &&
            state.institutionFinance.loadState.status == RequestState.Success,
        loadInstitutionFinanceFailure:
            state.institutionFinance.loadState &&
            state.institutionFinance.loadState.status == RequestState.Failure,
        loadInstitutionFinanceErrorCode:
            state.institutionFinance.loadState.errorCode,
        savingInstitutionFinance:
            state.institutionFinance.saveState &&
            state.institutionFinance.saveState.status == RequestState.Pending,
        saveInstitutionFinanceSuccess:
            state.institutionFinance.saveState &&
            state.institutionFinance.saveState.status == RequestState.Success,
        saveInstitutionFinanceFailure:
            state.institutionFinance.saveState &&
            state.institutionFinance.saveState.status == RequestState.Failure,
        validatingInstitutionFinance:
            state.institutionFinance.validateState &&
            state.institutionFinance.validateState.status ==
                RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.institutionFinance.lookups,
        form: state.reduxForms.institutionFinance,
        formState: state.institutionFinance.formState,
        formProperties: state.institutionFinance.formProperties,
        reduxFormState: state.reduxForms.formState.institutionFinance,
        validationFailures: institutionFinance
            ? state.institutionFinance.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<InstitutionFinancePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadInstitutionFinance: bindActionCreators(
            InstitutionFinanceActions.LoadInstitutionFinanceByInstitutionId,
            dispatch
        ),
        clearInstitutionFinance: bindActionCreators(
            InstitutionFinanceActions.Clear,
            dispatch
        ),
        saveInstitutionFinance: bindActionCreators(
            InstitutionFinanceActions.SaveInstitutionFinance,
            dispatch
        ),
        validateInstitutionFinance: bindActionCreators(
            InstitutionFinanceActions.ValidateInstitutionFinance,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(InstitutionFinancePage);
